import { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SEND_NPS_REVIEW, sendNpsReview } from '~/actions';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import {
  Button,
  Column,
  FloatingContainer,
  RadioOption,
  Row,
  SkipButton,
} from '~/commonComponents';
import useLoading from '~/hooks/useLoading';

import LogoDarkIcon from '~/images/logos/logoSmallDark.svg';

export const ReviewForm = () => {
  const [rating, setRating] = useState<number>(0);
  const isLoading = useLoading([SEND_NPS_REVIEW]);
  const btnActionRef = useRef<string>(undefined);
  const inputFeedbackRef = useRef<HTMLTextAreaElement>(undefined);

  const dispatch = useDispatch();

  const handleCancel = () => {
    btnActionRef.current = 'CANCEL';
    dispatch(sendNpsReview({ npsScore: -1 }));
  };

  const handleSubmit = () => {
    btnActionRef.current = 'SUBMIT';
    dispatch(
      sendNpsReview({
        message: inputFeedbackRef.current?.value,
        npsScore: rating,
      }),
    );
  };

  return (
    <Column backgroundColor={colors.neutral[50]} marginHorizontal={40}>
      <FloatingContainer>
        <img
          src={LogoDarkIcon}
          style={{ width: 74, height: 74, margin: '100px auto 20px' }}
          alt="logo_dark_icon"
        />
        <Typography as="span" bold color={colors.secondary[500]} size="heading_3" textAlign="center">
          Rate your experience!
        </Typography>
        <Typography as="span" color={colors.neutral[500]} textAlign="center" style={{ marginTop: 16 }}>
          Your opinion is important to us.
        </Typography>
        <Typography as="span" bold color={colors.neutral[600]} size="body_large" textAlign="center" style={{ marginTop: 40 }}>
          How likely are you to recommend GoTu to a colleague?
        </Typography>
        <Column margin="24px auto 0" width={384} gap={8}>
          <Row justifyContent="space-between">
            <Typography as="span" color={colors.neutral[500]}>Unlikely</Typography>
            <Typography as="span" color={colors.neutral[500]}>Very likely</Typography>
          </Row>
          <Row justifyContent="space-between">
            {Array(10)
              .fill(0)
              .map((_, index) => index + 1)
              .map((item, index) => {
                const isChecked = item === rating;

                return (
                  <RadioOption
                    key={`rate-${index}`}
                    checked={isChecked}
                    text={item}
                    textStyle={{
                      color: colors.neutral[600],
                      margin: 0,
                      marginTop: 4,
                    }}
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                    }}
                    onClick={() => setRating(item)}
                  />
                );
              })}
          </Row>
        </Column>
        <Column margin="48px auto 100px">
          <Typography as="span" bold color={colors.neutral[600]} size="body_large">
            How can we improve GoTu for you?
            <Typography as="span" color={colors.neutral[500]} style={{ marginLeft: 4 }}>
              (Optional)
            </Typography>
          </Typography>
          <textarea
            ref={inputFeedbackRef as any}
            className="textarea-style"
            style={{ height: 80, width: 724 }}
            placeholder="Tell us what you like, or what you didn’t"
          />
        </Column>
      </FloatingContainer>
      <Column alignItems="center" paddingVertical={24} paddingBottom={48} gap={20}>
        <Button
          color="primary"
          disabled={rating === 0}
          isLoading={isLoading && btnActionRef.current === 'SUBMIT'}
          style={{ width: 280 }}
          text="Submit"
          onClick={handleSubmit}
          dataCy="gotu-experience-submit-button"
        />
        <SkipButton
          isLoading={isLoading && btnActionRef.current === 'CANCEL'}
          onClick={handleCancel}
          dataCy="gotu-experience-skip-button"
        />
      </Column>
    </Column>
  );
};
